import { Component, Inject, signal, Signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EmailPreviewHydrationData, PreviewRenderingService } from '@galaxy/email-ui/email-builder';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { SelectedBusiness } from '../../../../shared/preview-business-contact-selector/preview-business-contact-selector.component';
import { EmailTemplateService, SenderType } from '@vendasta/campaigns';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContactData } from '@galaxy/email-ui/email-builder/src/components/business-contact-selector-modal/business-contact-selector-modal.component';
import { SenderInfo } from '../../../base-template-editing/template-editor-page/template-editor-page.component';

export interface SendPreviewEmailDialogData {
  business?: SelectedBusiness;
  subjectLine: string;
  emailHtmlContent: string;
  partnerID: string;
  marketID: string;
  locale?: string;
  contact?: ContactData;
  senderInfo: SenderInfo;
}

@Component({
  templateUrl: './send-preview-email-dialog.component.html',
  styleUrls: ['./send-preview-email-dialog.component.scss'],
  standalone: false,
})
export class SendPreviewEmailDialogComponent {
  hydrationData: EmailPreviewHydrationData;
  protected senderInfo: SenderInfo;
  protected userID: Signal<string>;
  protected loading: WritableSignal<boolean> = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SendPreviewEmailDialogData,
    @Inject('USER_ID') private userId$: Observable<string>,
    public dialogRef: MatDialogRef<SendPreviewEmailDialogComponent>,
    private emailPreviewService: PreviewRenderingService,
    private readonly alerts: SnackbarService,
    private readonly translate: TranslateService,
    private readonly campaignTemplateService: EmailTemplateService,
  ) {
    this.userID = toSignal(userId$);
    this.hydrationData = {
      business: data.business,
      userID: this.userID(),
      contactID: data.contact?.contactID,
      partnerID: this.data.partnerID,
      marketID: this.data.marketID,
    };
    this.senderInfo = data.senderInfo;
    this.emailPreviewService.setPreviewHydrationParams(this.hydrationData);
    this.emailPreviewService.setLocale(this.data.locale);
  }

  sendPreviewEmail(): void {
    this.loading.set(true);
    this.hydrateTemplate(this.userID(), this.hydrationData.contactID)
      .pipe(
        take(1),
        switchMap((hydratedTemplate) => {
          return this.campaignTemplateService.sendPreview(
            { type: SenderType.SENDER_TYPE_PARTNER, id: hydratedTemplate.partnerID },
            hydratedTemplate.content,
            hydratedTemplate.subject || 'Preview for unnamed template',
            hydratedTemplate.userID,
          );
        }),
        tap(() => this.handleSendSuccess()),
        catchError((err) => {
          this.handleSendError(err);
          return of(null);
        }),
      )
      .subscribe();
  }

  private hydrateTemplate(
    userID: string,
    contactID: string,
  ): Observable<{
    userID: string;
    partnerID: string;
    content: string;
    subject: string;
    namespace: string;
    contactID: string;
  }> {
    const hydratedContent = this.emailPreviewService.hydrateTemplate(this.data.emailHtmlContent);
    const hydratedSubject = this.emailPreviewService.hydrateSubject(this.data.subjectLine);
    return combineLatest([hydratedContent, hydratedSubject]).pipe(
      map(([content, subject]) => ({
        userID,
        partnerID: this.data.partnerID,
        namespace: this.data.partnerID,
        contactID: contactID ?? 'Contact',
        content,
        subject,
      })),
    );
  }

  handleSendSuccess(): void {
    this.loading.set(false);
    this.alerts.openSuccessSnack(this.translate.instant('CAMPAIGN_PREVIEW.SEND_SUCCESS'));
    this.dialogRef.close();
  }

  handleSendError(error: Error): void {
    this.loading.set(false);
    this.alerts.openErrorSnack(this.translate.instant('CAMPAIGN_PREVIEW.SEND_FAILED'));
    console.error(error);
  }
}
